/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import z from 'zod'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth-store'
import { getFreshModuleAccess } from '@/lib/nav-modules'
import { isAppHost } from '@/lib/host'
import { AuthenticatedLayout } from '@/components/layout'
import { PriceWorkbench } from '@/features/pricing/price-workbench'
import { PublicPricing } from '@/features/pricing'

// 定价 — a single hostname-aware route:
//   - api.4allapi.com (back/console): the price workbench inside the
//     authenticated console shell, login required.
//   - 4allapi.com (front/marketing): the pixel-faithful noya template pricing
//     decision page (PublicPricing), which provides its own PublicLayout.
const pricingSearchSchema = z.object({
  search: z.string().optional(),
  sort: z.string().optional(),
  vendor: z.string().optional(),
  group: z.string().optional(),
  quotaType: z.string().optional(),
  endpointType: z.string().optional(),
  tag: z.string().optional(),
  tokenUnit: z.enum(['M', 'K']).optional(),
  view: z.enum(['card', 'table']).optional().catch(undefined),
  rechargePrice: z.boolean().optional(),
})

function PricingPage() {
  if (isAppHost()) {
    return (
      <AuthenticatedLayout>
        <PriceWorkbench />
      </AuthenticatedLayout>
    )
  }

  // Front marketing domain — the template-faithful pricing decision page.
  return <PublicPricing />
}

export const Route = createFileRoute('/pricing/')({
  validateSearch: pricingSearchSchema,
  beforeLoad: async ({ location }) => {
    if (isAppHost()) {
      const { auth } = useAuthStore.getState()
      if (!auth.user) {
        throw redirect({ to: '/sign-in', search: { redirect: location.href } })
      }
      return
    }

    const access = await getFreshModuleAccess('pricing')
    if (!access.enabled) {
      throw redirect({ to: '/' })
    }
    if (access.requireAuth) {
      const { auth } = useAuthStore.getState()
      if (!auth.user) {
        throw redirect({ to: '/sign-in', search: { redirect: location.href } })
      }
    }
  },
  component: PricingPage,
})
